import {
  getMe,
  getUsersPermission,
  getAllUsers,
  createUser,
  type UpdateUserParams,
  updateUser,
} from "@/api/userService";
import { queryKeys } from "@/lib/queryKeys";
import { CACHE_TIME } from "@/lib/cacheConfig";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useQueryInvalidation } from "@/lib/queryInvalidation";
import { msalInstance } from "@/main";
import { useEffect, useRef, useState } from "react";

export const useGetMe = () => {
  // Check if user is authenticated with MSAL
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  const isAuthenticated = !!account;

  return useQuery({
    queryKey: queryKeys.user.me(),
    queryFn: getMe,
    staleTime: CACHE_TIME.USER_PROFILE,
    refetchOnReconnect: true,
    retry: 1,
    enabled: isAuthenticated, // Only run query if user is authenticated
  });
};

export const useGetUsersPermission = () => {
  return useQuery({
    queryKey: queryKeys.user.permission(),
    queryFn: getUsersPermission,
    staleTime: CACHE_TIME.MASTER_DATA,
  });
};

export const useGetAllUsers = () => {
  return useQuery({
    queryKey: queryKeys.user.list(),
    queryFn: getAllUsers,
    staleTime: CACHE_TIME.MASTER_DATA,
  });
};

export const useCreateUser = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.user.create(),
    mutationFn: ([userId, groupIds]: [string, number[]]) =>
      createUser(userId, groupIds),
    onSuccess: () => {
      queryInvalidation.invalidateUserPermission();
    },
  });
};

export const useUpdateUser = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.user.update(),
    mutationFn: (params: UpdateUserParams) => updateUser(params),
    onSuccess: () => {
      queryInvalidation.invalidateUserPermission();
    },
  });
};

export const useUserMeWebSocket = (onMessage?: (data: unknown) => void) => {
  const [isConnected, setIsConnected] = useState(false);
  const [wsData, setWsData] = useState<unknown>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const onMessageRef = useRef(onMessage);
  const retryRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onMessageRef.current = onMessage;
  });

  const { data: meData, refetch } = useGetMe();
  const pubsubUrl: string | undefined = meData?.pubsub_url;

  useEffect(() => {
    if (!pubsubUrl) return;

    let disposed = false;

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    // The pubsub_url carries a short-lived Azure Web PubSub token, so a dropped
    // connection can't just reuse it — refetch /me for a fresh url, then reconnect
    // with capped exponential backoff.
    const scheduleReconnect = () => {
      clearTimer();
      const delay = Math.min(1000 * 2 ** retryRef.current, 30000);
      retryRef.current += 1;
      timerRef.current = setTimeout(async () => {
        if (disposed) return;
        let nextUrl = pubsubUrl;
        try {
          const res = await refetch();
          if (res.data?.pubsub_url) nextUrl = res.data.pubsub_url;
        } catch {
          /* fall back to the last url */
        }
        connect(nextUrl);
      }, delay);
    };

    const connect = (url: string) => {
      if (disposed) return;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        setIsConnected(true);
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const data: unknown = JSON.parse(event.data as string);
          setWsData(data);
          onMessageRef.current?.(data);
        } catch {
          setWsData(event.data);
          onMessageRef.current?.(event.data);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        if (!disposed) scheduleReconnect();
      };

      // onclose fires after onerror, so let onclose own the reconnect.
      ws.onerror = () => setIsConnected(false);
    };

    connect(pubsubUrl);

    return () => {
      disposed = true;
      clearTimer();
      const ws = wsRef.current;
      wsRef.current = null;
      if (
        ws &&
        (ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING)
      ) {
        // Detach handlers so an intentional close doesn't schedule a reconnect.
        ws.onclose = null;
        ws.onerror = null;
        ws.close();
      }
    };
  }, [pubsubUrl, refetch]);

  return { isConnected, wsData };
};